import React, { useMemo } from 'react';
import { Row, Col, Typography, Skeleton, Empty, Progress } from 'antd';
import { UserAddOutlined, FacebookOutlined, InstagramOutlined, GoogleOutlined, TeamOutlined, LoginOutlined, YoutubeOutlined } from '@ant-design/icons';
import './known-by-sources.scss';

const { Title, Text } = Typography;

const KnownBySources = ({ knownByData = {}, loading = false }) => {
	const KNOWN_BY_MAPPING = [
		{ key: 'facebook', label: 'Facebook', icon: FacebookOutlined, color: '#1877F2', bgColor: 'rgba(24, 119, 242, 0.12)' },
		{ key: 'instagram', label: 'Instagram', icon: InstagramOutlined, color: '#E4405F', bgColor: 'rgba(228, 64, 95, 0.12)' },
		{ key: 'google', label: 'Google', icon: GoogleOutlined, color: '#4285F4', bgColor: 'rgba(66, 133, 244, 0.12)' },
		{ key: 'friend', label: 'Friend', icon: TeamOutlined, color: '#52C41A', bgColor: 'rgba(82, 196, 26, 0.12)' },
		{ key: 'walkIn', label: 'Walk-in', icon: LoginOutlined, color: '#FA8C16', bgColor: 'rgba(250, 140, 22, 0.12)' },
		{ key: 'youtube', label: 'YouTube', icon: YoutubeOutlined, color: '#FF0000', bgColor: 'rgba(255, 0, 0, 0.12)' },
	];

	const sources = useMemo(() => {
		return KNOWN_BY_MAPPING.map(({ key, label, icon, color, bgColor }) => ({
			label,
			count: knownByData?.[key] ?? 0,
			icon,
			color,
			bgColor,
		}));
	}, [knownByData]);

	const totalCount = useMemo(() => {
		return sources.reduce((sum, source) => sum + source.count, 0);
	}, [sources]);

	const hasData = useMemo(() => {
		return totalCount > 0;
	}, [totalCount]);

	if (loading) {
		return (
			<Row className="known-by-sources-card">
				<Col span={24}>
					<div style={{ padding: '16px' }}>
						<Skeleton paragraph={{ rows: 6 }} active />
					</div>
				</Col>
			</Row>
		);
	}

	if (!hasData) {
		return (
			<Row className="known-by-sources-card">
				<Col span={24}>
					<div className="known-by-header">
						<div className="header-icon-wrapper">
							<UserAddOutlined className="header-icon" />
						</div>
						<Title level={5} className="known-by-title">
							How Customers Heard About Us
						</Title>
					</div>
					<div className="empty-state">
						<Empty 
							description={<span className="empty-text">No data available</span>} 
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							imageStyle={{ opacity: 0.5 }}
						/>
					</div>
				</Col>
			</Row>
		);
	}

	return (
		<Row className="known-by-sources-card">
			<Col span={24}>
				<div className="known-by-header">
					<div className="header-icon-wrapper">
						<UserAddOutlined className="header-icon" />
					</div>
					<div className="header-content">
						<Title level={5} className="known-by-title">
							How Customers Heard About Us
						</Title>
						<Text className="total-count">Total: <span>{totalCount}</span></Text>
					</div>
				</div>
				<Row gutter={[0, 12]} className="sources-list">
					{sources.map(({ label, count, icon: IconComponent, color, bgColor }) => {
						const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0;
						return (
							<Col key={label} span={24}>
								<Row align="middle" gutter={[12, 0]} className="source-item" style={{ borderLeft: `4px solid ${color}` }}>
									<Col flex="0 0 auto">
										<div className="icon-wrapper" style={{ backgroundColor: bgColor }}>
											<IconComponent style={{ color, fontSize: '18px' }} />
										</div>
									</Col>
									<Col flex="1">
										<Row justify="space-between" align="middle" style={{ marginBottom: '6px' }}>
											<Text className="source-label">{label}</Text>
											<Text strong className="source-count">{count}</Text>
										</Row>
										<Progress 
											percent={percentage} 
											strokeColor={color}
											format={() => ''}
											size="small"
											className="source-progress"
										/>
									</Col>
								</Row>
							</Col>
						);
					})}
				</Row>
			</Col>
		</Row>
	);
};

export default KnownBySources;
